import React, { useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  TrendingDown as EasyIcon,
  AttachMoney as CostIcon,
  Schedule as TimeIcon,
  Person as PersonIcon,
  Room as RoomIcon
} from '@mui/icons-material';
import PropertyContext from '../contexts/PropertyContext';
import { generateAutoDispatch } from '../utils/autoDispatchAlgorithm';

const AutoDispatchModal = ({
  open,
  onClose,
  selectedEmployee,
  selectedDate,
  onDispatchGenerated,
  selectedProperty,
  employeeList
}) => {
  const [priorityMethod, setPriorityMethod] = useState('difficulty');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState('options'); // 'options', 'preview', 'confirmed'

  const { rooms, items, selectedClient } = useContext(PropertyContext);

  const handleGeneratePreview = async () => {
    if (!selectedEmployee || !selectedDate) {
      return;
    }

    setLoading(true);
    try {
      const employee = typeof selectedEmployee === 'string'
        ? { id: selectedEmployee }
        : selectedEmployee;

      const result = await generateAutoDispatch({
        employee,
        selectedDate: new Date(selectedDate),
        rooms,
        items,
        priorityMethod,
        clientId: selectedClient,
        selectedProperty: selectedProperty,
        allEmployees: employeeList
      });

      setPreview(result);
      setStep('preview');
    } catch (error) {
      console.error('Error generating auto-dispatch:', error);
      setPreview({
        success: false,
        message: 'Failed to generate dispatch schedule',
        tasks: []
      });
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDispatch = () => {
    if (preview?.success && preview.tasks) {
      onDispatchGenerated(preview.tasks);
      setStep('confirmed');
      setTimeout(() => {
        handleClose();
      }, 2000);
    }
  };

  const handleClose = () => {
    setStep('options');
    setPreview(null);
    setPriorityMethod('difficulty');
    onClose();
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTaskDuration = (task) => {
    const start = new Date(task.startTime);
    const end = new Date(task.endTime);
    const minutes = (end - start) / (1000 * 60);
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const renderOptionsStep = () => (
    <>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon color="primary" />
          Auto-Generate Dispatch Schedule
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Automatically generate a dispatch schedule with breaks, lunch, and optimized room assignments.
          </Typography>
        </Box>

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 2 }}>
            Room Prioritization Method
          </FormLabel>
          <RadioGroup
            value={priorityMethod}
            onChange={(e) => setPriorityMethod(e.target.value)}
          >
            <FormControlLabel
              value="difficulty"
              control={<Radio />}
              label={
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <EasyIcon fontSize="small" />
                    <Typography variant="body1">Difficulty-Based</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                    Prioritize rooms by lowest difficulty first (quickest wins)
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="cost"
              control={<Radio />}
              label={
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CostIcon fontSize="small" />
                    <Typography variant="body1">Cost-Based</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                    Prioritize rooms by lowest cost first (most economical)
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Schedule Features:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <TimeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Automatic breaks and lunch scheduling"
              secondary="Lunch after 4 hours, two 10-minute breaks optimally placed"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <RoomIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Smart room work sequencing"
              secondary="Respects maintenance/housekeeping order and completion status"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Employee type optimization"
              secondary="Portering for maintenance, appropriate task assignments"
            />
          </ListItem>
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleGeneratePreview}
          disabled={loading || !selectedEmployee || !selectedDate}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Generating...' : 'Generate Schedule'}
        </Button>
      </DialogActions>
    </>
  );

  const renderPreviewStep = () => (
    <>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <TimeIcon color="primary" />
          Schedule Preview
        </Box>
      </DialogTitle>

      <DialogContent>
        {preview?.success ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {preview.message}
            </Alert>

            <Typography variant="h6" gutterBottom>
              Generated Tasks ({preview.tasks.length}):
            </Typography>

            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {preview.tasks.map((task, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={task.task}
                          size="small"
                          color={
                            task.task === 'Room Turn' ? 'primary' :
                            task.task === 'Lunch' ? 'warning' :
                            task.task === 'Break' ? 'info' : 'default'
                          }
                        />
                        <Typography variant="body1">
                          {task.location}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(task.startTime)} - {formatTime(task.endTime)} ({getTaskDuration(task)})
                        </Typography>
                        {task.notes && (
                          <Typography variant="caption" color="text.secondary">
                            {task.notes}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {/* Load Balancing Information */}
            {preview.loadBalancingInfo && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Load Balancing Applied
                </Typography>
                <Typography variant="body2">
                  Working with {preview.loadBalancingInfo.totalEmployees} {selectedEmployee?.type} employees today.
                  Employee #{preview.loadBalancingInfo.employeeIndex + 1} assigned {preview.loadBalancingInfo.assignedRooms?.length || 0} rooms.
                </Typography>
                {preview.loadBalancingInfo.alreadyAssignedRooms?.size > 0 && (
                  <Typography variant="body2">
                    {preview.loadBalancingInfo.alreadyAssignedRooms.size} rooms already assigned to other employees.
                  </Typography>
                )}
              </Alert>
            )}

            {preview.unassignedRooms && preview.unassignedRooms.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {preview.unassignedRooms.length} rooms couldn't fit in today's schedule and will carry over to the next day.
              </Alert>
            )}
          </Box>
        ) : (
          <Alert severity="error">
            {preview?.message || 'Failed to generate schedule'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setStep('options')}>Back</Button>
        <Button onClick={handleClose}>Cancel</Button>
        {preview?.success && (
          <Button
            variant="contained"
            onClick={handleConfirmDispatch}
            color="success"
          >
            Apply Schedule
          </Button>
        )}
      </DialogActions>
    </>
  );

  const renderConfirmedStep = () => (
    <>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon color="success" />
          Schedule Applied!
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="success">
          Auto-dispatch schedule has been successfully applied. The page will refresh to show your new schedule.
        </Alert>
      </DialogContent>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      {step === 'options' && renderOptionsStep()}
      {step === 'preview' && renderPreviewStep()}
      {step === 'confirmed' && renderConfirmedStep()}
    </Dialog>
  );
};

export default AutoDispatchModal;